"use client";

import { c as _c } from "react/compiler-runtime";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownIcon, ArrowUpIcon, EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import { CompactMenu, type CompactMenuItem } from "@/components/compact-menu";
import { useAuth } from "@/components/auth/Auth";
import MyStreamWaveCurationCreateDialog from "@/components/brain/my-stream/tabs/MyStreamWaveCurationCreateDialog";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import CommonConfirmationModal from "@/components/utils/modal/CommonConfirmationModal";
import type { ApiGroup } from "@/generated/models/ApiGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import WaveGroupScope from "@/components/waves/specs/groups/group/WaveGroupScope";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import type { DropCurationMembership } from "@/hooks/drops/useDropCurations";
import { getWaveCurationsQueryKey, useWaveCurations } from "@/hooks/waves/useWaveCurations";
import { useWaveCurationReorderMutation } from "@/hooks/waves/useWaveCurationReorderMutation";
import { commonApiDelete, commonApiFetch } from "@/services/api/common-api";
const toScopeGroup = (group: ApiGroupFull): ApiGroup => ({
  id: group.id,
  name: group.name,
  author: group.created_by,
  is_hidden: !group.visible,
  is_direct_message: group.is_direct_message ?? false
});
export default function WaveActiveCurationSection(t0) {
  const $ = _c(91);
  const {
    wave
  } = t0;
  const {
    isApp
  } = useDeviceInfo();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    requestAuth,
    setToast
  } = useAuth();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("curation");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const activeCurationId = t1;
  const canManageCurations = wave.wave.authenticated_user_eligible_for_admin === true;
  const shouldLoadCurations = isApp || activeCurationId !== null;
  let t2;
  if ($[2] !== shouldLoadCurations || $[3] !== wave.id) {
    t2 = {
      waveId: wave.id,
      enabled: shouldLoadCurations
    };
    $[2] = shouldLoadCurations;
    $[3] = wave.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: t3
  } = useWaveCurations(t2);
  let t4;
  if ($[5] !== t3) {
    t4 = t3 === undefined ? [] : t3;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const curations = t4;
  let t5;
  if ($[7] !== wave.id) {
    t5 = {
      waveId: wave.id
    };
    $[7] = wave.id;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const {
    moveCuration,
    isPending: isCurationReorderPending,
    pendingCurationId
  } = useWaveCurationReorderMutation(t5);
  const resolvedActiveCurationId = activeCurationId ?? (isApp ? curations[0]?.id ?? null : null);
  let t6;
  if ($[9] !== curations || $[10] !== resolvedActiveCurationId) {
    t6 = curations.find(curation => curation.id === resolvedActiveCurationId) ?? null;
    $[9] = curations;
    $[10] = resolvedActiveCurationId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const activeCuration = t6;
  let t7;
  if ($[12] !== curations || $[13] !== resolvedActiveCurationId) {
    t7 = resolvedActiveCurationId ? curations.findIndex(curation_0 => curation_0.id === resolvedActiveCurationId) : -1;
    $[12] = curations;
    $[13] = resolvedActiveCurationId;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const activeCurationIndex = t7;
  const activeGroupId = activeCuration?.group_id ?? null;
  let t8;
  let t9;
  if ($[15] !== activeGroupId) {
    t8 = [QueryKey.GROUP, activeGroupId];
    t9 = async () => {
      if (!activeGroupId) {
        throw new Error("No active group selected.");
      }
      return await commonApiFetch({
        endpoint: `groups/${activeGroupId}`
      });
    };
    $[15] = activeGroupId;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  const t10 = !!activeGroupId;
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10,
      staleTime: 300000
    };
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const {
    data: activeGroup,
    isFetching: isFetchingActiveGroup
  } = useQuery(t11);
  let t12;
  if ($[22] !== pathname || $[23] !== router || $[24] !== searchParams) {
    t12 = curationId => {
      const params = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params.set("curation", curationId);
      } else {
        params.delete("curation");
      }
      const nextQuery = params.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[22] = pathname;
    $[23] = router;
    $[24] = searchParams;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const setSelectedCuration = t12;
  let t13;
  if ($[26] !== activeCuration || $[27] !== requestAuth || $[28] !== wave.id) {
    t13 = async () => {
      if (!activeCuration) {
        throw new Error("No active curation selected.");
      }
      const auth = await requestAuth();
      if (!auth.success) {
        throw new Error("Authentication was cancelled.");
      }
      await commonApiDelete({
        endpoint: `waves/${wave.id}/curations/${activeCuration.id}`
      });
    };
    $[26] = activeCuration;
    $[27] = requestAuth;
    $[28] = wave.id;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== activeCuration || $[31] !== queryClient || $[32] !== setSelectedCuration || $[33] !== setToast || $[34] !== wave.id) {
    t14 = async () => {
      if (!activeCuration) {
        return;
      }
      queryClient.setQueryData(getWaveCurationsQueryKey(wave.id), current => current?.filter(item => item.id !== activeCuration.id) ?? current);
      queryClient.setQueriesData({
        queryKey: ["drop-curations"]
      }, current_0 => current_0?.filter(item_0 => item_0.id !== activeCuration.id) ?? current_0);
      await queryClient.invalidateQueries({
        queryKey: getWaveCurationsQueryKey(wave.id)
      });
      await queryClient.invalidateQueries({
        queryKey: ["drop-curations"]
      });
      setToast({
        type: "success",
        message: "Curation deleted."
      });
      setIsDeleteOpen(false);
      setSelectedCuration(null);
    };
    $[30] = activeCuration;
    $[31] = queryClient;
    $[32] = setSelectedCuration;
    $[33] = setToast;
    $[34] = wave.id;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== setToast) {
    t15 = error => {
      const message = error instanceof Error ? error.message : "Failed to delete curation.";
      setToast({
        type: "error",
        message
      });
    };
    $[36] = setToast;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t15) {
    t16 = {
      mutationFn: t13,
      onSuccess: t14,
      onError: t15
    };
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const deleteMutation = useMutation(t16);
  const canMoveActiveCuration = canManageCurations && activeCuration !== null && curations.length > 1;
  const activeCurationIsReordering = activeCuration ? pendingCurationId === activeCuration.id : false;
  let t17;
  if ($[42] !== activeCuration || $[43] !== activeCurationId || $[44] !== curations || $[45] !== moveCuration || $[46] !== setSelectedCuration) {
    t17 = direction => {
      if (!activeCuration) {
        return;
      }
      if (!activeCurationId) {
        setSelectedCuration(activeCuration.id);
      }
      moveCuration({
        curation: activeCuration,
        direction,
        curations
      });
    };
    $[42] = activeCuration;
    $[43] = activeCurationId;
    $[44] = curations;
    $[45] = moveCuration;
    $[46] = setSelectedCuration;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  const moveActiveCuration = t17;
  let t18;
  if ($[48] !== activeCurationIndex || $[49] !== canMoveActiveCuration || $[50] !== curations.length || $[51] !== isCurationReorderPending || $[52] !== moveActiveCuration) {
    t18 = canMoveActiveCuration ? [{
      id: "move-up",
      label: "Move up",
      icon: <ArrowUpIcon className="tw-size-4" />,
      disabled: activeCurationIndex <= 0 || isCurationReorderPending,
      onSelect: () => moveActiveCuration("previous")
    }, {
      id: "move-down",
      label: "Move down",
      icon: <ArrowDownIcon className="tw-size-4" />,
      disabled: activeCurationIndex < 0 || activeCurationIndex >= curations.length - 1 || isCurationReorderPending,
      onSelect: () => moveActiveCuration("next")
    }] : [];
    $[48] = activeCurationIndex;
    $[49] = canMoveActiveCuration;
    $[50] = curations.length;
    $[51] = isCurationReorderPending;
    $[52] = moveActiveCuration;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsEditOpen(true);
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  const t20 = !activeGroup;
  let t21;
  if ($[55] !== t20) {
    t21 = {
      id: "edit",
      label: "Edit curation",
      onSelect: t19,
      disabled: t20
    };
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      id: "delete",
      label: "Delete curation",
      onSelect: () => setIsDeleteOpen(true),
      className: "tw-text-red desktop-hover:hover:tw-text-red"
    };
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t18 || $[59] !== t21) {
    t23 = [...t18, t21, t22];
    $[58] = t18;
    $[59] = t21;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const menuItems = t23;
  if (!isApp && (!activeCurationId || !activeCuration)) {
    return null;
  }
  const desktopActiveCuration = isApp ? null : activeCuration;
  let t24;
  if ($[61] !== activeCuration?.group_id || $[62] !== activeCurationIsReordering || $[63] !== activeGroup || $[64] !== canManageCurations || $[65] !== curations || $[66] !== desktopActiveCuration?.group_id || $[67] !== desktopActiveCuration?.name || $[68] !== isApp || $[69] !== isFetchingActiveGroup || $[70] !== menuItems || $[71] !== resolvedActiveCurationId || $[72] !== setSelectedCuration) {
    t24 = isApp ? curations.length > 0 && <div className="tw-pb-4"><div className="tw-flex tw-items-start tw-justify-between tw-gap-x-6 tw-px-4 tw-pt-4"><p className="tw-mb-0 tw-text-base tw-font-semibold tw-tracking-tight tw-text-iron-200">Curation</p></div><div className="tw-mt-2 tw-flex tw-flex-col tw-gap-y-1 tw-px-4">{curations.map(curation_1 => {
          const isActive = curation_1.id === resolvedActiveCurationId;
          return <div key={curation_1.id} className={clsx("tw-rounded-xl tw-border tw-border-solid tw-transition-colors tw-duration-200", isActive ? "tw-border-iron-800 tw-bg-iron-950/70" : "tw-border-transparent")}><div className="tw-flex tw-items-center"><button type="button" onClick={() => setSelectedCuration(curation_1.id)} className={clsx("tw-flex tw-h-10 tw-min-w-0 tw-flex-1 tw-items-center tw-rounded-xl tw-border-0 tw-bg-transparent tw-px-4 tw-text-left tw-transition-colors tw-duration-200", isActive ? "tw-text-iron-50" : "tw-text-iron-300 desktop-hover:hover:tw-bg-iron-900/55 desktop-hover:hover:tw-text-iron-100")} aria-pressed={isActive}><span className={clsx("tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-leading-tight", isActive ? "tw-font-semibold" : "tw-font-medium")}>{curation_1.name}</span></button>{isActive && canManageCurations && <CompactMenu triggerClassName="tw-mr-1 tw-flex tw-size-8 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-100" trigger={<EllipsisHorizontalIcon className="tw-size-5" />} aria-label="Active curation options" items={menuItems} menuWidthClassName="tw-w-44" disabled={activeCurationIsReordering} />}</div>{isActive && <div className="tw-flex tw-min-h-6 tw-items-center tw-justify-between tw-gap-x-4 tw-pb-2.5 tw-pl-4 tw-pr-4 tw-text-sm"><span className="tw-font-medium tw-text-iron-500">Group</span>{activeGroup ? <WaveGroupScope group={toScopeGroup(activeGroup)} /> : <span className="tw-max-w-40 tw-truncate tw-text-sm tw-font-medium tw-text-iron-200">{isFetchingActiveGroup ? "Loading..." : activeCuration?.group_id}</span>}</div>}</div>;
        })}</div></div> : <div className="tw-pb-4"><div className="tw-flex tw-items-start tw-justify-between tw-gap-x-6 tw-px-4 tw-pt-4"><div className="tw-min-w-0"><p className="tw-mb-1 tw-text-base tw-font-semibold tw-tracking-tight tw-text-iron-200">Curation</p><p className="tw-mb-0 tw-truncate tw-text-sm tw-font-medium tw-text-iron-400">{desktopActiveCuration?.name}</p></div></div><div className="tw-mt-2 tw-flex tw-flex-col tw-gap-y-2 tw-px-4"><div className="tw-flex tw-h-6 tw-items-center tw-justify-between tw-text-sm"><span className="tw-font-medium tw-text-iron-500">Group</span>{activeGroup ? <WaveGroupScope group={toScopeGroup(activeGroup)} /> : <span className="tw-max-w-40 tw-truncate tw-text-sm tw-font-medium tw-text-iron-200">{isFetchingActiveGroup ? "Loading..." : desktopActiveCuration?.group_id}</span>}</div></div></div>;
    $[61] = activeCuration?.group_id;
    $[62] = activeCurationIsReordering;
    $[63] = activeGroup;
    $[64] = canManageCurations;
    $[65] = curations;
    $[66] = desktopActiveCuration?.group_id;
    $[67] = desktopActiveCuration?.name;
    $[68] = isApp;
    $[69] = isFetchingActiveGroup;
    $[70] = menuItems;
    $[71] = resolvedActiveCurationId;
    $[72] = setSelectedCuration;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  let t25;
  if ($[74] !== activeCuration || $[75] !== activeGroup || $[76] !== isEditOpen || $[77] !== wave) {
    t25 = isEditOpen && activeCuration && activeGroup && <MyStreamWaveCurationCreateDialog key={`${activeCuration.id}:${activeGroup.id}`} wave={wave} isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} onSaved={_temp} curation={activeCuration} initialGroup={activeGroup} />;
    $[74] = activeCuration;
    $[75] = activeGroup;
    $[76] = isEditOpen;
    $[77] = wave;
    $[78] = t25;
  } else {
    t25 = $[78];
  }
  let t26;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setIsDeleteOpen(false);
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== deleteMutation) {
    t27 = () => deleteMutation.mutate();
    $[80] = deleteMutation;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  const t28 = activeCuration ? `Delete "${activeCuration.name}" from this wave?` : "Delete this curation from this wave?";
  let t29;
  if ($[82] !== deleteMutation.isPending || $[83] !== isDeleteOpen || $[84] !== t27 || $[85] !== t28) {
    t29 = <CommonConfirmationModal isOpen={isDeleteOpen} onClose={t26} onConfirm={t27} title="Delete curation" message={t28} confirmText="Delete" isConfirming={deleteMutation.isPending} />;
    $[82] = deleteMutation.isPending;
    $[83] = isDeleteOpen;
    $[84] = t27;
    $[85] = t28;
    $[86] = t29;
  } else {
    t29 = $[86];
  }
  let t30;
  if ($[87] !== t24 || $[88] !== t25 || $[89] !== t29) {
    t30 = <>{t24}{t25}{t29}</>;
    $[87] = t24;
    $[88] = t25;
    $[89] = t29;
    $[90] = t30;
  } else {
    t30 = $[90];
  }
  return t30;
}
function _temp() {}